import React from 'react';
import { Divider } from '@mui/material';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  AI_COLORS, AI_FONTS,
  ImgSlot, TipBox, CheckItem, VideoDrive,
  PageHero, ContentWrap, SectionTitle, SubTitle, Card,
} from '../shared';

// Imagens (importadas)
import img18 from '../../../imgs/adulto-idosos/18.png';
import img19 from '../../../imgs/adulto-idosos/19.png';
import img20 from '../../../imgs/adulto-idosos/20.png';
import img21 from '../../../imgs/adulto-idosos/21.png';
import img22 from '../../../imgs/adulto-idosos/22.png';
import img23 from '../../../imgs/adulto-idosos/23.png';
import img24 from '../../../imgs/adulto-idosos/24.png';
import img26 from '../../../imgs/adulto-idosos/26.png';
import img27 from '../../../imgs/adulto-idosos/27.png';
import img28 from '../../../imgs/adulto-idosos/28.png';
import img29 from '../../../imgs/adulto-idosos/29.png';
import img30 from '../../../imgs/adulto-idosos/30.png';
import img31 from '../../../imgs/adulto-idosos/31.png';
import img55 from '../../../imgs/adulto-idosos/55.png';
import img56 from '../../../imgs/adulto-idosos/56.png';

const sinaisImg = [
  {
    img: img26,
    legenda: 'Estomatite protética — vermelhidão no céu da boca, na região coberta pela prótese',
  },
  {
    img: img56,
    legenda: 'Estomatite protética — vermelhidão no céu da boca, na região coberta pela prótese',
  },
  {
    img: img27,
    legenda: 'Queilite angular — feridas ou rachaduras nos cantos da boca',
  },
  {
    img: img28,
    legenda: 'Hiperplasia fibrosa inflamatória - aumento da gengiva próximo da dentadura, causada pelo atrito',
  },
  {
    img: img29,
    legenda: 'Úlcera traumática — ferida no céu da boca, causada por machucado ou atrito da prótese',
  },
];

export default function ProtesesImplantes() {
  const isMobile = useIsMobile();

  const typeCard = (bg) => ({
    background: bg,
    borderRadius: 14,
    padding: '20px 24px',
    marginBottom: 20,
  });

  const typeTitle = {
    fontFamily: AI_FONTS.title,
    color: AI_COLORS.blue900,
    fontSize: '1.2rem',
    marginTop: 0,
    marginBottom: 10,
  };

  return (
    <div style={{ fontFamily: AI_FONTS.body, background: 'transparent' }}>
      <PageHero
        title="Próteses Dentárias e Implantes Dentários"
        subtitle="As próteses ajudam a substituir dentes perdidos, recuperando a função de mastigar, falar e sorrir com conforto."
        audio="proteses-implantes.m4a"
      />

      <ContentWrap>
        {/* O que são */}
        <SectionTitle style={{ marginTop: 0 }}>O que são?</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          As <strong>próteses</strong> ajudam a substituir dentes perdidos. Elas podem ser{' '}
          <strong>removíveis</strong> (dentaduras) ou <strong>fixas</strong> (coroas). Os{' '}
          <strong>implantes dentários</strong> são estruturas colocadas no osso para substituir a
          raiz do dente perdido.
        </p>

        <Card variant={0} style={{ marginTop: 8 }}>
          <p
            style={{
              fontFamily: AI_FONTS.title,
              color: AI_COLORS.blue900,
              fontSize: '1rem',
              margin: '0 0 8px',
            }}
          >
            Quando são indicadas?
          </p>
          <p style={{ margin: '0 0 8px' }}>
            Podem ser indicadas quando a pessoa perdeu um ou mais dentes, dificultando:
          </p>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 2 }}>
            <li>Mastigar</li>
            <li>Falar</li>
            <li>Sorrir</li>
            <li>Realizar atividades do dia a dia</li>
          </ul>
        </Card>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Tipos de próteses */}
        <SectionTitle>Tipos de Próteses Dentárias</SectionTitle>

        {/* Coroa (prótese fixa) */}
        <div style={typeCard(AI_COLORS.bgBlue50)}>
          <h3 style={typeTitle}>Coroa (prótese fixa)</h3>
          <p style={{ margin: '0 0 8px', lineHeight: 1.7 }}>
            É uma prótese usada para substituir ou recuperar um dente perdido ou danificado. Ela
            fica fixa na sua boca, ou seja, você não precisa retirar para limpar ou colocar
            novamente no dia a dia.
          </p>
          <p style={{ margin: '0 0 8px', lineHeight: 1.7 }}>
            Pode ser usada para recuperar um dente que está muito quebrado. A coroa tem aparência
            semelhante à de um dente natural e permite mastigar e sorrir com mais conforto.
          </p>

          <ImgSlot
            filename={img18}
            alt="Coroa (prótese fixa) — exemplo"
            second={img19}
          />

          Assista o vídeo abaixo para entender, de forma simples e ilustrativa, como é feita a colocação de uma coroa


          <VideoDrive id="1SW0p40ZHB31AYNYqkadZxf69B8gmpRrS" title="Coroa (prótese fixa)" />

          <p style={{ fontWeight: 600, color: AI_COLORS.blue800, margin: '12px 0 6px' }}>
            Como cuidar:
          </p>
          <ul style={{ padding: 0, margin: 0 }}>
            <CheckItem>Escove normalmente</CheckItem>
            <CheckItem>Passe fio dental</CheckItem>
            <CheckItem>
              Visite o cirurgião dentista regularmente, de preferência a cada 6 meses
            </CheckItem>
          </ul>
        </div>

        {/* Dentadura (prótese total removível) */}
        <div style={typeCard(AI_COLORS.bgPurple50)}>
          <h3 style={typeTitle}>Dentadura (prótese total removível)</h3>
          <p style={{ margin: '0 0 8px', lineHeight: 1.7 }}>
            Ela pode ser indicada quando você perdeu todos os dentes da parte de cima, da parte de
            baixo ou de ambas. Substitui todos os dentes perdidos e pode ser retirada para limpeza.
          </p>
          <p style={{ fontWeight: 600, color: AI_COLORS.blue800, margin: '8px 0 6px' }}>Ajuda a:</p>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 2 }}>
            <li>Mastigar melhor</li>
            <li>Melhorar a fala</li>
            <li>Devolver a estética do sorriso</li>
          </ul>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 12,
              marginTop: 12,
            }}
          >
            <ImgSlot filename={img20} alt="Dentadura (prótese total removível)" maxWidth={280} />
            <ImgSlot filename={img21} alt="Dentadura (prótese total removível)" maxWidth={280} />
            <ImgSlot filename={img22} alt="Dentadura (prótese total removível)" maxWidth={280} />
          </div>
        </div>

        {/* Ponte (prótese parcial removível) */}
        <div style={typeCard(AI_COLORS.bgBlue50)}>
          <h3 style={typeTitle}>Ponte (prótese parcial removível)</h3>
          <p style={{ margin: '0 0 8px', lineHeight: 1.7 }}>
            Ela pode ser indicada quando você perdeu alguns dentes, mas ainda possui dentes naturais
            na boca. Pode ser retirada para higienização e recolocada depois.
          </p>
          <p style={{ fontWeight: 600, color: AI_COLORS.blue800, margin: '8px 0 6px' }}>Ajuda a:</p>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 2 }}>
            <li>Melhorar a mastigação</li>
            <li>Facilitar a fala</li>
            <li>Preencher os espaços deixados pelos dentes perdidos</li>
          </ul>

          <ImgSlot
            filename={img23}
            alt="Ponte (prótese parcial removível) — exemplo"
            second={img24}
          />
        </div>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Como cuidar */}
        <SectionTitle>Como cuidar?</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          As próteses removíveis precisam de cuidados diários para se manterem limpas e seguras:
        </p>
        <ul style={{ padding: 0, margin: '0 0 8px' }}>
          <CheckItem>Retire e limpe diariamente</CheckItem>
          <CheckItem>
            Use escova própria ou escova macia — prefira detergente neutro em vez de pasta de dente
          </CheckItem>
          <CheckItem>Higienize após as refeições</CheckItem>
          <CheckItem>
            Antes de dormir, retire a prótese, faça a limpeza, seque bem e guarde em um recipiente
            limpo e fechado, como mostrado na imagem abaixo
          </CheckItem>
          <CheckItem>
            Visite o cirurgião dentista regularmente, de preferência a cada 6 meses
          </CheckItem>
        </ul>

        <ImgSlot
          filename={img55}
          alt="Como guardar a prótese — recipiente limpo e fechado"
          caption="Antes de dormir, guarde a prótese limpa e seca em um recipiente fechado"
          maxWidth={360}
        />

        Assista o vídeo abaixo para entender, de forma simples e ilustrativa, como higienizar a sua dentadura ou ponte

        <VideoDrive id="16LaRddJVJIvapjmR6V8muM_AWJj4X8oX" title="Como cuidar da prótese" referencia="Produzido pela autora utilizando a ferramenta Google Gemini (2026)." />

        <TipBox>Isso ajuda a manter a prótese limpa e a cuidar da saúde da sua boca.</TipBox> 

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Sinais de atenção */}
        <SectionTitle>Atenção aos sinais na sua boca</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          O uso de próteses dentárias que não estão bem adaptadas, estão desgastadas ou não são
          higienizadas corretamente pode causar alguns problemas na boca. Fique atento a sinais
          como:
        </p>
        <ul style={{ paddingLeft: 20, lineHeight: 2 }}>
          <li>Vermelhidão na gengiva ou no céu da boca</li>
          <li>Feridas ou machucados que demoram para melhorar</li>
          <li>Dor ou desconforto ao usar a prótese</li>
          <li>Caroços ou aumento de tecido na gengiva</li>
          <li>Rachaduras ou feridas nos cantos da boca</li>
        </ul>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 16,
            margin: '16px 0',
          }}
        >
          {sinaisImg.map((s, i) => (
            <div
              key={i}
              style={{
                background: AI_COLORS.pink50,
                borderRadius: 12,
                padding: '14px 18px',
                borderLeft: '3px solid #EF9A9A',
              }}
            >
              <ImgSlot filename={s.img} alt={s.legenda} caption={s.legenda} maxWidth={320} referencia="Neville et al. (2021)." />
            </div>
          ))}
        </div>

        <TipBox icon="⚠️">
          Se você notar algum desses sinais, procure um cirurgião dentista para avaliação.
        </TipBox>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Implantes */}
        <SectionTitle>Implantes Dentários</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          Se você perdeu um dente, o implante dentário pode ser uma opção para substituir esse dente
          perdido. Ele funciona como uma <strong>raiz artificial</strong>, colocada no osso para dar
          apoio ao novo dente. Depois, sobre o implante é colocado um dente artificial com aparência
          muito semelhante a um dente natural, ajudando você a voltar a sorrir, falar e mastigar com
          mais conforto.
        </p>

        <ImgSlot
          filename={img30}
          alt="Implante dentário — estrutura e colocação"
          caption="O implante funciona como uma raiz artificial que dá apoio ao novo dente"
          maxWidth={420}
        />

        <p style={{ lineHeight: 1.75 }}>
          O vídeo abaixo mostra, de forma simples e ilustrativa, como acontece a colocação de um
          implante dentário:
        </p>
        <VideoDrive id="1guJJdJV12BoDZ7e7XTS3vvouwCEeCKD0" title="Colocação de um implante dentário" />

        <SubTitle>Como cuidar do implante:</SubTitle>
        <ul style={{ padding: 0, margin: 0 }}>
          <CheckItem>Escove normalmente</CheckItem>
          <CheckItem>Passe fio dental</CheckItem>
          <CheckItem>
            Visite o cirurgião dentista regularmente, de preferência a cada 6 meses
          </CheckItem>
        </ul>

        <TipBox>
          Os implantes precisam de cuidados diários, como escovação, uso de fio dental e
          acompanhamento de 6 em 6 meses com o dentista para manter a saúde da boca e dos tecidos ao
          redor dos implantes.
        </TipBox>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Prótese protocolo */}
        <SectionTitle>Prótese protocolo sobre implantes</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          Se você perdeu todos os dentes de uma arcada (de cima ou de baixo) ou de ambas, existe uma
          opção que funciona como uma <strong>"dentadura fixa"</strong>. Nesse tratamento, alguns
          implantes são colocados para dar apoio a uma prótese com vários dentes. Ela fica presa aos
          implantes, trazendo mais firmeza e segurança para falar, sorrir e mastigar.
        </p>
        <p style={{ lineHeight: 1.75 }}>
          Diferente da dentadura tradicional, você não precisa retirar para colocar novamente no dia
          a dia — a remoção, quando necessária, é feita pelo dentista.
        </p>

        <ImgSlot
          filename={img31}
          alt="Prótese protocolo sobre implantes"
          caption="Prótese protocolo — firmeza e conforto semelhante aos dentes naturais"
          maxWidth={420}
        />

        <p style={{ lineHeight: 1.75 }}>
          O vídeo abaixo mostra, de forma simples e ilustrativa, como acontece a colocação de uma
          prótese protocolo sobre implantes:
        </p>
        <VideoDrive
          id="1I0NIB_gdRYVtnVChwi9SMFfGdtesjdnh"
          title="Colocação de prótese protocolo sobre implantes"
        />
      </ContentWrap>
    </div>
  );
}
